import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BRANCHES } from "@/lib/branches";
import { useAuth } from "@/context/AuthContext";
import { inr, fmtDate, today } from "@/lib/format";
import { printDayBook, printDayBookSummary, printBranchExpenseCompare } from "@/lib/print";
import { Printer, Trash2, Plus, BookOpen, CalendarRange, Download, BarChart3, Building2, Target, MessageCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const CATEGORIES = ["Freight Collection", "Office Rent", "Salary / Wages", "Diesel / Fuel", "Loading / Unloading", "Stationery", "Electricity", "Telephone / Internet", "Vehicle Maintenance", "Bank Charges", "Misc Expense"];

export default function DayBook() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [date, setDate] = useState(today());
  const [office, setOffice] = useState(isAdmin ? "All" : user?.office || "Chennai");
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const blank = { entry_type: "Payment", mode: "Cash", category: "", particulars: "", amount: "", ref_no: "" };
  const [form, setForm] = useState(blank);
  const [sumOpen, setSumOpen] = useState(false);
  const [exOpen, setExOpen] = useState(false);
  const [sumMonth, setSumMonth] = useState(today().slice(0, 7));
  const [summary, setSummary] = useState(null);
  const [chartOpen, setChartOpen] = useState(false);
  const [chartMonth, setChartMonth] = useState(today().slice(0, 7));
  const [cmpOpen, setCmpOpen] = useState(false);
  const [cmpMonth, setCmpMonth] = useState(today().slice(0, 7));
  const [budgets, setBudgets] = useState({});
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [budgetForm, setBudgetForm] = useState({});
  const [monthSpend, setMonthSpend] = useState([]);
  useEffect(() => {
    api.get("/daybook/budgets").then((r) => setBudgets(r.data?.budgets || {})).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/daybook/summary", { params: { month: date.slice(0, 7) } }).then((r) => setMonthSpend(r.data?.rows || [])).catch(() => setMonthSpend([]));
  }, [date, data]); // eslint-disable-line react-hooks/exhaustive-deps
  const budgetAlerts = monthSpend
    .filter((r) => Number(budgets[r.office]) > 0 && (office === "All" || r.office === office || !isAdmin))
    .map((r) => {
      const budget = Number(budgets[r.office]);
      const pct = Math.round((r.payments / budget) * 100);
      return { office: r.office, spent: r.payments, budget, pct };
    })
    .filter((b) => b.pct >= 80)
    .sort((a, b) => b.pct - a.pct);
  const saveBudgets = async () => {
    try {
      const r = await api.put("/daybook/budgets", { budgets: budgetForm });
      setBudgets(r.data?.budgets || {});
      setBudgetOpen(false);
      toast.success("Monthly budgets saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const [allEntries, setAllEntries] = useState(null);
  useEffect(() => {
    if (chartOpen || cmpOpen) api.get("/daybook", { params: { office: cmpOpen ? "All" : office } }).then((r) => setAllEntries(r.data?.entries || [])).catch(() => setAllEntries([]));
  }, [chartOpen, cmpOpen, office]); // eslint-disable-line react-hooks/exhaustive-deps
  const branchCompare = () => {
    const cats = {};
    const branchSet = new Set();
    (allEntries || []).forEach((e) => {
      if (e.entry_type !== "Payment" || (e.date || "").slice(0, 7) !== cmpMonth) return;
      const c = (e.category || "").trim() || "Uncategorized";
      const b = e.office || "Chennai";
      branchSet.add(b);
      const g = cats[c] || (cats[c] = { category: c, per: {}, total: 0 });
      g.per[b] = (g.per[b] || 0) + (Number(e.amount) || 0);
      g.total += Number(e.amount) || 0;
    });
    const branchList = [...branchSet].sort();
    const rowsOut = Object.values(cats)
      .map((g) => ({ ...g, maxBranch: branchList.reduce((mx, b) => ((g.per[b] || 0) > (g.per[mx] || 0) ? b : mx), branchList[0]) }))
      .sort((a, b) => b.total - a.total);
    const totals = {};
    branchList.forEach((b) => { totals[b] = rowsOut.reduce((s, r) => s + (r.per[b] || 0), 0); });
    return { month: cmpMonth, branches: branchList, rows: rowsOut, totals };
  };
  const prevMonth = (m) => {
    const [y, mm] = m.split("-").map(Number);
    return mm === 1 ? `${y - 1}-12` : `${y}-${String(mm - 1).padStart(2, "0")}`;
  };
  const chartData = () => {
    const pm = prevMonth(chartMonth);
    const cats = {};
    (allEntries || []).forEach((e) => {
      if (e.entry_type !== "Payment") return;
      const m = (e.date || "").slice(0, 7);
      if (m !== chartMonth && m !== pm) return;
      const c = (e.category || "").trim() || "Uncategorized";
      const g = cats[c] || (cats[c] = { category: c, current: 0, previous: 0 });
      g[m === chartMonth ? "current" : "previous"] += Number(e.amount) || 0;
    });
    return Object.values(cats).sort((a, b) => b.current - a.current);
  };
  useEffect(() => {
    if (sumOpen) api.get("/daybook/summary", { params: { month: sumMonth } }).then((r) => setSummary(r.data)).catch(() => setSummary(null));
  }, [sumOpen, sumMonth]);

  const load = async () => {
    try {
      const r = await api.get("/daybook", { params: { date, office } });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [date, office]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!Number(form.amount)) return toast.error("Enter a valid amount");
    setSaving(true);
    try {
      await api.post("/daybook", { ...form, amount: Number(form.amount), date, office: office === "All" ? "" : office });
      setForm(blank);
      toast.success("Entry added");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const exportMonthExcel = async () => {
    const month = date.slice(0, 7);
    try {
      const r = await api.get("/daybook", { params: { office } });
      const all = (r.data?.entries || []).filter((e) => (e.date || "").startsWith(month)).sort((a, b) => (a.date + (a.created_at || "")).localeCompare(b.date + (b.created_at || "")));
      if (!all.length) return toast.error(`No day book entries in ${month}`);
      const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
      const lines = [["Date", "Branch", "Type", "Mode", "Category", "Particulars", "Ref No.", "Entered By", "Receipt", "Payment"].join(",")];
      let rIn = 0, rOut = 0;
      all.forEach((e) => {
        const amt = Number(e.amount) || 0;
        if (e.entry_type === "Receipt") rIn += amt; else rOut += amt;
        lines.push([e.date, e.office, e.entry_type, e.mode, e.category || "", e.particulars || "", e.ref_no || "", e.created_by || "", e.entry_type === "Receipt" ? amt : "", e.entry_type === "Payment" ? amt : ""].map(cell).join(","));
      });
      lines.push(["TOTAL", "", "", "", "", "", "", "", rIn, rOut].map(cell).join(","));
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `day-book-${office === "All" ? "all-branches" : office.toLowerCase()}-${month}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${all.length} entries for ${month}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const del = async (r) => {
    try {
      await api.delete(`/daybook/${r.id}`);
      toast.success("Entry deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const entries = data?.entries || [];
  const t = data?.totals || {};
  const cards = [
    { label: "Opening Cash", value: data?.opening?.Cash || 0 },
    { label: "Opening Bank", value: data?.opening?.Bank || 0 },
    { label: "Receipts", value: (t.receipt_cash || 0) + (t.receipt_bank || 0), cls: "text-emerald-700" },
    { label: "Payments", value: (t.payment_cash || 0) + (t.payment_bank || 0), cls: "text-red-700" },
    ...(data?.extra_charges?.total ? [{ label: `Extra Charges (${data.extra_charges.count} challan${data.extra_charges.count > 1 ? "s" : ""})`, value: data.extra_charges.total, cls: "text-orange-700", onClick: () => setExOpen(true) }] : []),
    { label: "Closing Cash", value: data?.closing?.Cash || 0, cls: "font-bold" },
    { label: "Closing Bank", value: data?.closing?.Bank || 0, cls: "font-bold" },
  ];

  return (
    <div data-testid="daybook-page">
      <PageHeader
        title="Branch Day Book"
        subtitle="Daily cash / bank / expense entries per branch with opening & closing balances."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Input type="date" className="w-40" value={date} onChange={(e) => setDate(e.target.value)} data-testid="daybook-date" />
            {isAdmin && (
              <Select value={office} onValueChange={setOffice}>
                <SelectTrigger className="w-[150px]" data-testid="daybook-branch-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["All", ...BRANCHES].map((b) => <SelectItem key={b} value={b}>{b === "All" ? "All Branches" : b}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {isAdmin && BRANCHES.map((b) => {
              const n = (data?.entries || []).filter((e) => (e.office || "Chennai") === b).length;
              const active = office === b;
              return n || active ? (
                <button
                  type="button"
                  key={b}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                  title={active ? "Tap to show all branches again" : `Tap to show only ${b}'s day book`}
                  onClick={() => setOffice(active ? "All" : b)}
                  data-testid={`daybook-branch-count-${b.toLowerCase()}`}
                >
                  {b} <b className={active ? "" : "text-primary"}>{n}</b>
                </button>
              ) : null;
            })}
            {isAdmin && (
              <Button variant="outline" onClick={() => { setBudgetForm(Object.fromEntries(BRANCHES.map((b) => [b, budgets[b] || ""]))); setBudgetOpen(true); }} data-testid="daybook-budget-btn">
                <Target className="mr-2 h-4 w-4" /> Budget
              </Button>
            )}
            {isAdmin && (
              <Button variant="outline" onClick={() => setCmpOpen(true)} data-testid="daybook-compare-btn">
                <Building2 className="mr-2 h-4 w-4" /> Branch Compare
              </Button>
            )}
            <Button variant="outline" onClick={() => setChartOpen(true)} data-testid="daybook-chart-btn">
              <BarChart3 className="mr-2 h-4 w-4" /> Category Chart
            </Button>
            <Button variant="outline" onClick={exportMonthExcel} data-testid="daybook-excel-btn">
              <Download className="mr-2 h-4 w-4" /> Excel (Month)
            </Button>
            <Button variant="outline" onClick={() => setSumOpen(true)} data-testid="daybook-summary-btn">
              <CalendarRange className="mr-2 h-4 w-4" /> Monthly Summary
            </Button>
            <Button variant="outline" onClick={() => printDayBook(entries, { date, office: isAdmin ? office : user?.office || "Chennai", opening: data?.opening, totals: t, closing: data?.closing, settlements: data?.settlements })} disabled={!data} data-testid="daybook-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
            <Button
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              disabled={!data}
              onClick={() => {
                const st = data?.settlements || { rows: [], total: 0 };
                const msg = [
                  `*NTC — Day Book Closing (${fmtDate(date)})*`,
                  `Branch: ${isAdmin ? (office === "All" ? "All Branches" : office) : user?.office || "Chennai"}`,
                  "",
                  `Opening: Cash ${inr(data?.opening?.Cash || 0)} | Bank ${inr(data?.opening?.Bank || 0)}`,
                  `Receipts: ${inr((t.receipt_cash || 0) + (t.receipt_bank || 0))} (${entries.filter((e) => e.entry_type === "Receipt").length} entries)`,
                  `Payments: ${inr((t.payment_cash || 0) + (t.payment_bank || 0))} (${entries.filter((e) => e.entry_type === "Payment").length} entries)`,
                  ...(data?.extra_charges?.total ? [`Extra Charges on Challans: ${inr(data.extra_charges.total)} (${data.extra_charges.count} challan${data.extra_charges.count > 1 ? "s" : ""})`] : []),
                  `Settled Cash to Vehicle Owners: ${inr(st.total || 0)}${st.rows.length ? ` (${st.rows.length} challan${st.rows.length > 1 ? "s" : ""}: ${st.rows.slice(0, 5).map((s) => String(s.challan_no || "").replace(/^VHC[-\s]?/i, "")).join(", ")})` : ""}`,
                  ...(st.rows || []).filter((s) => s.release_reason).slice(0, 5).map((s) => `  ⚠ Challan ${String(s.challan_no || "").replace(/^VHC[-\s]?/i, "")} — admin release${s.release_by ? ` by ${s.release_by}` : ""}: ${s.release_reason}`),
                  `Closing: Cash ${inr(data?.closing?.Cash || 0)} | Bank ${inr(data?.closing?.Bank || 0)}`,
                  "",
                  "Day closed — match cash in hand with the closing balance.",
                ].join("\n");
                const digits = String(localStorage.getItem("ntc_company_mobile") || "").replace(/\D/g, "");
                const target = digits ? (digits.length === 10 ? "91" + digits : digits) : "";
                window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
              }}
              data-testid="daybook-wa-btn"
            >
              <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
            </Button>
          </div>
        }
      />

      {budgetAlerts.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="daybook-budget-alerts">
          {budgetAlerts.map((b) => (
            <div
              key={b.office}
              className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-sm font-semibold ${b.pct >= 100 ? "border-red-300 bg-red-50 text-red-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}
              data-testid="daybook-budget-alert"
            >
              <span>{b.pct >= 100 ? "⛔" : "⚠"} {b.office}: {inr(b.spent)} spent of {inr(b.budget)} budget ({b.pct}%) this month{b.pct >= 100 ? ` — OVER by ${inr(b.spent - b.budget)}` : " — nearing the limit"}</span>
              <span className="h-2 w-40 overflow-hidden rounded-full bg-white">
                <span className={`block h-full ${b.pct >= 100 ? "bg-red-600" : "bg-amber-500"}`} style={{ width: `${Math.min(b.pct, 100)}%` }} />
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {cards.map((c) => (
          <div
            key={c.label}
            onClick={c.onClick}
            className={`rounded-lg border bg-white px-3 py-2 ${c.onClick ? "cursor-pointer transition-colors hover:border-orange-300 hover:bg-orange-50" : ""}`}
            title={c.onClick ? "Tap to see which challans make up this figure" : undefined}
            data-testid={`daybook-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{c.label}{c.onClick ? " →" : ""}</div>
            <div className={`text-sm tabular-nums ${c.cls || ""}`}>{inr(c.value)}</div>
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-2 rounded-lg border bg-slate-50 p-3" data-testid="daybook-entry-form">
        <div className="grid gap-1">
          <Label className="text-[11px]">Type</Label>
          <Select value={form.entry_type} onValueChange={(v) => setForm((f) => ({ ...f, entry_type: v }))}>
            <SelectTrigger className="h-9 w-[120px]" data-testid="daybook-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="Receipt">Receipt (In)</SelectItem><SelectItem value="Payment">Payment (Out)</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="grid gap-1">
          <Label className="text-[11px]">Mode</Label>
          <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v }))}>
            <SelectTrigger className="h-9 w-[100px]" data-testid="daybook-mode"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="Cash">Cash</SelectItem><SelectItem value="Bank">Bank</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="grid gap-1">
          <Label className="text-[11px]">Category</Label>
          <Input className="h-9 w-44" list="daybook-categories" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} placeholder="e.g. Office Rent" data-testid="daybook-category" />
          <datalist id="daybook-categories">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
        </div>
        <div className="grid flex-1 gap-1">
          <Label className="text-[11px]">Particulars</Label>
          <Input className="h-9 min-w-48" value={form.particulars} onChange={(e) => setForm((f) => ({ ...f, particulars: e.target.value }))} placeholder="Description" data-testid="daybook-particulars" />
        </div>
        <div className="grid gap-1">
          <Label className="text-[11px]">Amount (₹)</Label>
          <Input className="h-9 w-28" type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid="daybook-amount" />
        </div>
        <div className="grid gap-1">
          <Label className="text-[11px]">Ref No.</Label>
          <Input className="h-9 w-28" value={form.ref_no} onChange={(e) => setForm((f) => ({ ...f, ref_no: e.target.value }))} placeholder="Optional" data-testid="daybook-ref" />
        </div>
        <Button className="h-9" onClick={save} disabled={saving} data-testid="daybook-add-btn">
          <Plus className="mr-1.5 h-4 w-4" /> {saving ? "Saving…" : "Add Entry"}
        </Button>
      </div>

      <TableShell>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.15em] text-slate-500">
            <tr className="[&>th]:px-3 [&>th]:py-2 text-left">
              <th>Sl</th><th>Branch</th><th>Type</th><th>Mode</th><th>Category</th><th>Particulars</th><th>Ref No.</th><th>By</th><th className="text-right">Receipt</th><th className="text-right">Payment</th><th></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((r, i) => (
              <tr key={r.id} className="border-t [&>td]:px-3 [&>td]:py-2" data-testid="daybook-row">
                <td>{i + 1}</td>
                <td>{r.office}</td>
                <td>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.entry_type === "Receipt" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{r.entry_type}</span>
                </td>
                <td>{r.mode}</td>
                <td>{r.category || "—"}</td>
                <td>{r.particulars || "—"}</td>
                <td>{r.ref_no || "—"}</td>
                <td className="text-xs text-muted-foreground">{r.created_by || "—"}</td>
                <td className="text-right tabular-nums text-emerald-700">{r.entry_type === "Receipt" ? inr(r.amount) : "—"}</td>
                <td className="text-right tabular-nums text-red-700">{r.entry_type === "Payment" ? inr(r.amount) : "—"}</td>
                <td>
                  {isAdmin && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => del(r)} data-testid="daybook-delete-btn">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {data && entries.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-10 text-center text-muted-foreground"><BookOpen className="mx-auto mb-2 h-6 w-6" />No entries for {fmtDate(date)}. Add the day's first entry above.</td></tr>
            )}
            {entries.length > 0 && (
              <tr className="border-t bg-slate-100 font-bold [&>td]:px-3 [&>td]:py-2" data-testid="daybook-total-row">
                <td colSpan={8} className="text-right text-xs uppercase">Day Total</td>
                <td className="text-right tabular-nums text-emerald-700">{inr((t.receipt_cash || 0) + (t.receipt_bank || 0))}</td>
                <td className="text-right tabular-nums text-red-700">{inr((t.payment_cash || 0) + (t.payment_bank || 0))}</td>
                <td></td>
              </tr>
            )}
          </tbody>
        </table>
      </TableShell>

      {(data?.settlements?.rows || []).length > 0 && (
        <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-4" data-testid="daybook-settlements">
          <div className="flex items-center gap-2 text-sm font-bold text-emerald-800">
            Vehicle Settlements — cash paid to owners ({data.settlements.rows.length})
            <span className="ml-auto rounded-full bg-emerald-600 px-2.5 py-0.5 text-xs font-bold text-white" data-testid="daybook-settlements-total">{inr(data.settlements.total)}</span>
          </div>
          <div className="mt-2 space-y-1">
            {data.settlements.rows.map((s, i) => (
              <div key={i} className="flex flex-wrap items-center gap-x-2 text-xs text-emerald-800" data-testid="daybook-settlement-row">
                <span className="font-bold">{String(s.challan_no || "").replace(/^VHC[-\s]?/i, "")}</span>
                <span>· {s.vehicle_no || "—"}</span>
                <span>· {s.party || "—"}</span>
                <span>· by {s.by || "—"}</span>
                {s.release_reason && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800" title={`Balance released under admin approval${s.release_by ? ` by ${s.release_by}` : ""}`} data-testid="daybook-release-note">
                    Admin release: {s.release_reason}
                  </span>
                )}
                <span className="ml-auto font-bold tabular-nums">{inr(s.amount)}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-emerald-700">Shown from the Hire Challan settlement stamps — prints on the Day Book sheet so one sheet closes the day.</p>
        </div>
      )}

      <Dialog open={exOpen} onOpenChange={setExOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[760px]" data-testid="daybook-charges-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Extra Charges — {fmtDate(date)}</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto rounded border">
            <table className="w-full text-xs">
              <thead className="bg-muted">
                <tr className="text-left [&>th]:p-2">
                  <th>Challan No.</th><th>Vehicle</th><th>Branch</th><th className="text-right">Unloading</th><th className="text-right">Challan</th><th className="text-right">Extra Wt</th><th className="text-right">Extra Dly</th><th className="text-right">Total</th><th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {(data?.extra_charges?.rows || []).map((r, i) => (
                  <tr key={i} className="border-t [&>td]:p-2" data-testid="daybook-charges-row">
                    <td className="font-semibold">{String(r.challan_no || "—").replace(/^VHC[-\s]?/i, "")}</td>
                    <td className="whitespace-nowrap">{r.vehicle_no || "—"}</td>
                    <td>{r.office}</td>
                    <td className="text-right tabular-nums">{r.unloading ? inr(r.unloading) : "—"}</td>
                    <td className="text-right tabular-nums">{r.challan ? inr(r.challan) : "—"}</td>
                    <td className="text-right tabular-nums">{r.extra_wt ? inr(r.extra_wt) : "—"}</td>
                    <td className="text-right tabular-nums">{r.extra_dly ? inr(r.extra_dly) : "—"}</td>
                    <td className="text-right font-bold tabular-nums text-orange-700">{inr(r.total)}</td>
                    <td className="max-w-[160px] text-[11px] text-slate-600">{r.reason || "—"}</td>
                  </tr>
                ))}
                {(data?.extra_charges?.rows || []).length > 0 && (
                  <tr className="border-t bg-slate-100 font-bold [&>td]:p-2">
                    <td colSpan={7} className="text-right uppercase">Total</td>
                    <td className="text-right tabular-nums text-orange-700">{inr(data?.extra_charges?.total || 0)}</td>
                    <td></td>
                  </tr>
                )}
                {(data?.extra_charges?.rows || []).length === 0 && (
                  <tr><td colSpan={9} className="p-4 text-center text-muted-foreground">No extra charges on challans loaded this day.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={sumOpen} onOpenChange={setSumOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[640px]" data-testid="daybook-summary-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Day Book — Monthly Summary</DialogTitle>
          </DialogHeader>
          <div className="flex items-end gap-3">
            <div className="grid gap-1.5">
              <Label>Month</Label>
              <Input type="month" className="w-44" value={sumMonth} onChange={(e) => setSumMonth(e.target.value)} data-testid="daybook-summary-month" />
            </div>
            <Button variant="outline" size="sm" className="ml-auto mb-1" onClick={() => summary && printDayBookSummary(summary)} disabled={!summary} data-testid="daybook-summary-print-btn">
              <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
            </Button>
          </div>
          <div className="rounded border">
            <table className="w-full text-xs">
              <thead className="bg-muted">
                <tr className="text-left [&>th]:p-2">
                  <th>Branch</th><th className="text-right">Opening</th><th className="text-right">Receipts</th><th className="text-right">Payments</th><th className="text-right">Closing</th>
                </tr>
              </thead>
              <tbody>
                {(summary?.rows || []).map((r) => (
                  <tr key={r.office} className="border-t [&>td]:p-2" data-testid="daybook-summary-row">
                    <td className="font-semibold">{r.office}</td>
                    <td className="text-right tabular-nums">{inr(r.opening)}</td>
                    <td className="text-right tabular-nums text-emerald-700">{inr(r.receipts)}</td>
                    <td className="text-right tabular-nums text-red-700">{inr(r.payments)}</td>
                    <td className="text-right font-bold tabular-nums">{inr(r.closing)}</td>
                  </tr>
                ))}
                {summary && (summary.rows || []).length === 0 && (
                  <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No entries for this month.</td></tr>
                )}
                {(summary?.rows || []).length > 0 && (
                  <tr className="border-t bg-slate-100 font-bold [&>td]:p-2" data-testid="daybook-summary-total">
                    <td>TOTAL</td>
                    <td className="text-right tabular-nums">{inr(summary.totals.opening)}</td>
                    <td className="text-right tabular-nums text-emerald-700">{inr(summary.totals.receipts)}</td>
                    <td className="text-right tabular-nums text-red-700">{inr(summary.totals.payments)}</td>
                    <td className="text-right tabular-nums">{inr(summary.totals.closing)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={chartOpen} onOpenChange={setChartOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[820px]" data-testid="daybook-chart-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Expense Category Chart</DialogTitle>
          </DialogHeader>
          <div className="flex items-end gap-3">
            <div className="grid gap-1.5">
              <Label>Month</Label>
              <Input type="month" className="w-44" value={chartMonth} onChange={(e) => setChartMonth(e.target.value)} data-testid="daybook-chart-month" />
            </div>
            <p className="mb-1 ml-auto text-[11px] text-muted-foreground">Payments only · vs previous month ({prevMonth(chartMonth)})</p>
          </div>
          {(() => {
            const data = chartData();
            if (allEntries === null) return <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>;
            if (!data.length) return <div className="py-10 text-center text-sm text-muted-foreground" data-testid="daybook-chart-empty">No expense entries in {chartMonth} or the previous month.</div>;
            return (
              <>
                <div className="h-72 w-full" data-testid="daybook-chart-area">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="category" angle={-30} textAnchor="end" interval={0} tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                      <ChartTooltip formatter={(v) => inr(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="previous" name={`Prev (${prevMonth(chartMonth)})`} fill="#94a3b8" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="current" name={chartMonth} fill="#dc2626" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="rounded border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted">
                      <tr className="text-left [&>th]:p-2"><th>Category</th><th className="text-right">Prev Month</th><th className="text-right">This Month</th><th className="text-right">Change</th></tr>
                    </thead>
                    <tbody>
                      {data.map((r) => {
                        const diff = r.current - r.previous;
                        const pct = r.previous ? Math.round((diff / r.previous) * 100) : null;
                        return (
                          <tr key={r.category} className="border-t [&>td]:p-2" data-testid="daybook-chart-row">
                            <td className="font-semibold">{r.category}</td>
                            <td className="text-right tabular-nums">{inr(r.previous)}</td>
                            <td className="text-right tabular-nums">{inr(r.current)}</td>
                            <td className={`text-right font-bold tabular-nums ${diff > 0 ? "text-red-600" : diff < 0 ? "text-emerald-600" : "text-slate-500"}`}>
                              {diff === 0 ? "—" : `${diff > 0 ? "▲" : "▼"} ${inr(Math.abs(diff))}${pct != null ? ` (${Math.abs(pct)}%)` : ""}`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
      <Dialog open={cmpOpen} onOpenChange={setCmpOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[860px]" data-testid="daybook-compare-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Branch Expense Comparison</DialogTitle>
          </DialogHeader>
          {(() => {
            const d = branchCompare();
            return (
              <>
                <div className="flex items-end gap-3">
                  <div className="grid gap-1.5">
                    <Label>Month</Label>
                    <Input type="month" className="w-44" value={cmpMonth} onChange={(e) => setCmpMonth(e.target.value)} data-testid="daybook-compare-month" />
                  </div>
                  <Button variant="outline" size="sm" className="ml-auto mb-1" onClick={() => printBranchExpenseCompare(d)} disabled={!d.rows.length} data-testid="daybook-compare-print-btn">
                    <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">Day Book payments per category — the highest-spending branch in each row is highlighted red.</p>
                <div className="overflow-x-auto rounded border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted">
                      <tr className="text-left [&>th]:p-2">
                        <th>Category</th>
                        {d.branches.map((b) => <th key={b} className="text-right">{b}</th>)}
                        <th className="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.rows.map((r) => (
                        <tr key={r.category} className="border-t [&>td]:p-2" data-testid="daybook-compare-row">
                          <td className="font-semibold">{r.category}</td>
                          {d.branches.map((b) => (
                            <td key={b} className={`text-right tabular-nums ${r.maxBranch === b && r.per[b] ? "bg-red-50 font-bold text-red-700" : ""}`}>{r.per[b] ? inr(r.per[b]) : "—"}</td>
                          ))}
                          <td className="text-right font-bold tabular-nums">{inr(r.total)}</td>
                        </tr>
                      ))}
                      {allEntries !== null && d.rows.length === 0 && (
                        <tr><td colSpan={d.branches.length + 2} className="p-4 text-center text-muted-foreground" data-testid="daybook-compare-empty">No expense entries for {cmpMonth}.</td></tr>
                      )}
                      {d.rows.length > 0 && (
                        <tr className="border-t bg-slate-100 font-bold [&>td]:p-2" data-testid="daybook-compare-total">
                          <td>TOTAL</td>
                          {d.branches.map((b) => <td key={b} className="text-right tabular-nums">{inr(d.totals[b] || 0)}</td>)}
                          <td className="text-right tabular-nums">{inr(Object.values(d.totals).reduce((a, v) => a + v, 0))}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
      <Dialog open={budgetOpen} onOpenChange={setBudgetOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[480px]" data-testid="daybook-budget-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Monthly Expense Budget per Branch</DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-muted-foreground">Set the monthly Day Book payment limit for each branch. A warning shows at 80% and a red alert when spending crosses the budget. Leave blank for no limit.</p>
          <div className="grid gap-2">
            {BRANCHES.map((b) => (
              <div key={b} className="flex items-center gap-3">
                <Label className="w-32 text-xs font-semibold">{b}</Label>
                <Input
                  type="number"
                  className="h-9"
                  placeholder="No limit"
                  value={budgetForm[b] ?? ""}
                  onChange={(e) => setBudgetForm((f) => ({ ...f, [b]: e.target.value }))}
                  data-testid={`budget-input-${b.toLowerCase()}`}
                />
              </div>
            ))}
          </div>
          <Button onClick={saveBudgets} data-testid="daybook-budget-save-btn">Save Budgets</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
